import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, Car, TrendingUp, ArrowRight } from "lucide-react";

const CITIES = ["Tijuana", "Rosarito", "Ensenada", "Mexicali", "San Diego"];
const CATEGORIES = ["Económico", "Sedán", "SUV", "Pickup", "Lujo", "Eléctrico"];
const COMMISSION_PCT = 0.15;
// Maximum daily price a host can plug into the calculator. Aligned with
// the pricing cap in the vehicle-listing form so estimates stay realistic.
const MAX_DAILY_PRICE = 10000;

function moneyFmt(n) {
  return `$${Math.round(n).toLocaleString("es-MX")} MXN`;
}

function ScenarioRow({ label, days, dailyPrice }) {
  const gross = dailyPrice * days;
  const commission = gross * COMMISSION_PCT;
  const net = gross - commission;
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid={`scenario-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="font-heading font-semibold text-agave">{label}</p>
        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">
          {days} días/mes
        </span>
      </div>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-neutral-600">Ingreso bruto</span><span>{moneyFmt(gross)}</span></div>
        <div className="flex justify-between text-neutral-500"><span>Comisión RivoNes (15%)</span><span>-{moneyFmt(commission)}</span></div>
        <div className="flex justify-between font-bold text-terracotta border-t pt-1 mt-1"><span>Ganancia mensual</span><span>{moneyFmt(net)}</span></div>
        <div className="flex justify-between text-xs text-neutral-500"><span>Anual (×12)</span><span>{moneyFmt(net * 12)}</span></div>
      </div>
    </div>
  );
}

export default function CalculadoraGananciasPage() {
  const [city, setCity] = useState(CITIES[0]);
  const [category, setCategory] = useState(CATEGORIES[1]);
  const [dailyPrice, setDailyPrice] = useState(800);
  const [days, setDays] = useState(15);

  const results = useMemo(() => {
    const gross = Math.max(0, dailyPrice) * Math.max(0, days);
    const commission = gross * COMMISSION_PCT;
    const net = gross - commission;
    return { gross, commission, net, annual: net * 12 };
  }, [dailyPrice, days]);

  return (
    <div className="min-h-screen bg-sand pb-16" data-testid="calculadora-page">
      <div className="container-main py-10 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-terracotta/10 text-terracotta flex items-center justify-center">
            <Calculator className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold text-agave">Calculadora de Ganancias</h1>
            <p className="text-sm text-neutral-600">Estima cuánto puedes ganar publicando tu auto en RivoNes.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Inputs */}
          <div className="card-base space-y-4">
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Ciudad</label>
              <select value={city} onChange={(e) => setCity(e.target.value)} className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm text-agave bg-white" data-testid="calc-city">
                {CITIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Categoría del vehículo</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm text-agave bg-white" data-testid="calc-category">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">
                Precio diario (MXN) — máx. ${MAX_DAILY_PRICE.toLocaleString("es-MX")}
              </label>
              <input
                type="number"
                inputMode="numeric"
                min="0"
                max={MAX_DAILY_PRICE}
                value={dailyPrice}
                onChange={(e) => {
                  const raw = parseInt(e.target.value) || 0;
                  // Clamp to the pricing cap so hosts don't over-estimate
                  // with unrealistic numbers ($100k/day rentals aren't a
                  // thing on the platform).
                  setDailyPrice(Math.min(Math.max(raw, 0), MAX_DAILY_PRICE));
                }}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-base text-agave font-semibold bg-white placeholder:text-neutral-400"
                data-testid="calc-daily-price"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Días de renta estimados al mes: <span className="text-agave font-semibold">{days}</span></label>
              <input type="range" min="1" max="30" value={days} onChange={(e) => setDays(parseInt(e.target.value))} className="w-full accent-terracotta" data-testid="calc-days" />
            </div>
          </div>

          {/* Live results */}
          <div className="card-base bg-gradient-to-br from-amber-50 to-white">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-terracotta" />
              <h2 className="font-heading font-semibold text-lg text-agave">Tu estimación</h2>
            </div>
            <div className="space-y-2 text-sm" data-testid="calc-results">
              <div className="flex justify-between"><span className="text-neutral-600">Ingreso bruto mensual</span><span className="font-semibold">{moneyFmt(results.gross)}</span></div>
              <div className="flex justify-between text-neutral-500"><span>Comisión RivoNes (15%)</span><span>-{moneyFmt(results.commission)}</span></div>
              <div className="flex justify-between text-xl font-bold text-terracotta border-t pt-3 mt-3"><span>Ganancia mensual</span><span data-testid="calc-net-monthly">{moneyFmt(results.net)}</span></div>
              <div className="flex justify-between text-base font-semibold text-emerald-700"><span>Ganancia anual estimada</span><span data-testid="calc-net-annual">{moneyFmt(results.annual)}</span></div>
            </div>
          </div>
        </div>

        {/* 3 scenarios */}
        <h2 className="text-xl font-heading font-bold text-agave mt-10 mb-4">Tres escenarios típicos</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ScenarioRow label="Conservador" days={8} dailyPrice={dailyPrice} />
          <ScenarioRow label="Promedio" days={15} dailyPrice={dailyPrice} />
          <ScenarioRow label="Alta demanda" days={22} dailyPrice={dailyPrice} />
        </div>

        <p className="text-xs text-neutral-500 italic mt-6">
          Las ganancias mostradas son estimaciones y pueden variar según demanda, ubicación, temporada, disponibilidad, precio del vehículo, condiciones del mercado, impuestos, mantenimiento y otros gastos.
        </p>

        <Link to="/host/dashboard" className="btn-primary inline-flex items-center gap-2 mt-6" data-testid="calc-cta">
          <Car className="w-4 h-4" />
          Publicar mi Auto
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
